import { createSlice } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import { TradingBattleState, AppThunk } from 'state/types'
import { getBalanceAmount } from 'utils/formatBalance'
import { fetchClaimInformation } from './fetchClaimInformation'
import { fetchCompetitionStatus } from './fetchCompetitionStatus'

interface TradesInfo {
  id: number
  status: number
  userCount: number
  volumeUSD: number
  volumeCRO: number
  txCount: number
}

interface User {
  id: number
  volumeUSD: number
  txCount: number
  currentRank: number
  previousRank: number
}

interface Block1h {
  blockNumber: number
  timestamp: number
}

const initialState: TradingBattleState = {
  block1h: {
    blockNumber: null,
    timestamp: null,
  },
  currentTrader: {
    id: null,
    volumeUSD: null,
    txCount: null,
    currentRank: null,
    previousRank: null,
  },
  topTenTraders: [],
  tradesInfo: {
    id: null,
    status: null,
    userCount: null,
    volumeUSD: null,
    volumeCRO: null,
    txCount: null,
  },
  competitionStatus: null,
  claimInformation: {
    hasRegistered: false,
    hasUserClaimed: false,
    vvsReward: null,
  },
}

export const fetchCompetitionStatusAsync = (): AppThunk => async (dispatch) => {
  const competitionStatus = await fetchCompetitionStatus()
  dispatch(setTradingBattleCompetitionStatus(competitionStatus))
}

export const fetchClaimInformationAsync =
  (account: string): AppThunk =>
  async (dispatch) => {
    const [hasRegistered, hasUserClaimed, vvsReward] = await fetchClaimInformation(account)
    const formattedReward = getBalanceAmount(new BigNumber(vvsReward._hex)).toJSON()
    dispatch(setTradingBattleClaimInformation({ hasRegistered, hasUserClaimed, vvsReward: formattedReward }))
  }

export const setClaimInformation =
  (account: string): AppThunk =>
  async (dispatch) => {
    const [hasRegistered, hasUserClaimed, vvsReward] = await fetchClaimInformation(account)
    const formattedReward = getBalanceAmount(new BigNumber(vvsReward._hex)).toJSON()
    dispatch(setTradingBattleClaimInformation({ hasRegistered, hasUserClaimed, vvsReward: formattedReward }))
  }

export const setTradesInfo =
  (tradesInfo: TradesInfo): AppThunk =>
  async (dispatch) => {
    dispatch(setTradingBattleTradesInfo(tradesInfo))
  }

export const updateCurrentTrader =
  (trader: User): AppThunk =>
  async (dispatch) => {
    dispatch(updateTradingBattleCurrentTrader(trader))
  }

export const updateTopTenTraders =
  (topTenTraders: User[]): AppThunk =>
  async (dispatch) => {
    dispatch(updateTradingBattleTopTenTraders(topTenTraders))
  }

export const updateBlock1h =
  (block1h: Block1h): AppThunk =>
  async (dispatch) => {
    dispatch(updateTradingBattleBlock1h(block1h))
  }

export const TradingBattleSlice = createSlice({
  name: 'TradingBattle',
  initialState,
  reducers: {
    setTradingBattleCompetitionStatus: (state, action) => {
      state.competitionStatus = action.payload
    },
    setTradingBattleClaimInformation: (state, action) => {
      state.claimInformation = { ...action.payload }
    },
    setTradingBattleTradesInfo: (state, action) => {
      state.tradesInfo = { ...action.payload }
    },
    updateTradingBattleCurrentTrader: (state, action) => {
      const currentTrader = action.payload
      if (currentTrader) state.currentTrader = { ...currentTrader }
    },
    updateTradingBattleTopTenTraders: (state, action) => {
      const topTenTraders = action.payload
      if (topTenTraders) state.topTenTraders = [...topTenTraders]
    },
    updateTradingBattleBlock1h: (state, action) => {
      state.block1h = { ...action.payload }
    },
  },
})

// Actions
export const {
  setTradingBattleCompetitionStatus,
  setTradingBattleClaimInformation,
  setTradingBattleTradesInfo,
  updateTradingBattleCurrentTrader,
  updateTradingBattleTopTenTraders,
  updateTradingBattleBlock1h,
} = TradingBattleSlice.actions

export default TradingBattleSlice.reducer
